import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { staffLabel, labelFor, STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, Users, CheckCircle2, AlertTriangle, Zap } from 'lucide-react';

export default function BulkAssignShiftsPage() {
  const [openShifts, setOpenShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState(new Set());
  const [assignments, setAssignments] = useState({}); // { shiftId: staffId }
  const [bulkStaffId, setBulkStaffId] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [results, setResults] = useState(null);
  const [dateFilter, setDateFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: '100', status: 'OPEN' });
      if (dateFilter) { params.set('start', dateFilter); params.set('end', dateFilter); }
      const [shiftsRes, staffRes] = await Promise.all([
        api.get(`/roster/shifts?${params}`),
        api.get('/staff?pageSize=100'),
      ]);
      setOpenShifts(shiftsRes.data.items || []);
      setStaff(staffRes.data.items || []);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [dateFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleShift = (id) => {
    setSelectedShifts(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const selectAll = () => setSelectedShifts(new Set(openShifts.map(s => s.id)));
  const clearSelection = () => { setSelectedShifts(new Set()); setAssignments({}); setBulkStaffId(''); };

  const setStaffForShift = (shiftId, staffId) => {
    setAssignments(prev => ({ ...prev, [shiftId]: staffId }));
  };

  const applyBulkStaff = () => {
    if (!bulkStaffId) return;
    const newAssignments = {};
    selectedShifts.forEach(id => { newAssignments[id] = bulkStaffId; });
    setAssignments(prev => ({ ...prev, ...newAssignments }));
    toast.success(`Staff assigned to ${selectedShifts.size} shifts`);
  };

  const handleBulkAssign = async () => {
    const toAssign = Array.from(selectedShifts)
      .filter(id => assignments[id])
      .map(id => ({ shift_id: id, staff_id: assignments[id] }));

    if (toAssign.length === 0) { toast.error('No shifts have staff assigned'); return; }

    setAssigning(true);
    try {
      const { data } = await api.post('/roster/shifts/bulk-assign', { assignments: toAssign });
      setResults(data.data);
      toast.success(`${data.data.assigned} of ${data.data.total} shifts assigned`);
      loadData();
      setSelectedShifts(new Set());
      setAssignments({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk assign failed'); }
    setAssigning(false);
  };

  const getStaffName = (id) => {
    const s = staff.find(st => st.id === id);
    return s ? staffLabel(s) : id;
  };

  const readyCount = Array.from(selectedShifts).filter(id => assignments[id]).length;

  return (
    <div className="space-y-6" data-testid="bulk-assign-shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Zap className="w-6 h-6" />Bulk Assign Shifts</h1>
          <p className="text-muted-foreground mt-1">{openShifts.length} open shifts available</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <Label className="text-xs">Filter by Date</Label>
          <TrinitiDatePicker value={dateFilter} onChange={v => setDateFilter(v)} className="w-44" testId="date-filter" />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-shifts">Select All ({openShifts.length})</Button>
          {selectedShifts.size > 0 && <Button variant="ghost" size="sm" onClick={clearSelection}>Clear Selection</Button>}
        </div>
      </div>

      {/* Bulk staff assignment toolbar */}
      {selectedShifts.size > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-3">
            <div className="flex items-center gap-4 flex-wrap">
              <Badge variant="default" className="text-sm">{selectedShifts.size} shifts selected</Badge>
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Assign all to:</Label>
                <div className="w-56">
                  <AsyncSearchSelect
                    endpoint="/staff"
                    value={bulkStaffId}
                    onChange={v => setBulkStaffId(v)}
                    getOptionLabel={staffLabel}
                    selectedLabel={labelFor(staff, bulkStaffId, staffLabel)}
                    placeholder="Select staff member"
                    searchPlaceholder={STAFF_SEARCH_HINT}
                    emptyText="No staff match that search"
                    testId="bulk-staff-select"
                  />
                </div>
                <Button size="sm" onClick={applyBulkStaff} disabled={!bulkStaffId} data-testid="apply-bulk-staff">Apply</Button>
              </div>
              <Button className="ml-auto" onClick={handleBulkAssign} disabled={assigning || readyCount === 0} data-testid="bulk-assign-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />{assigning ? 'Assigning...' : `Assign ${readyCount} Shifts`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results banner */}
      {results && (
        <Card className={results.failed > 0 ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}>
          <CardContent className="py-3 flex items-center gap-3">
            {results.failed > 0 ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <CheckCircle2 className="w-5 h-5 text-green-600" />}
            <p className="text-sm font-medium">
              {results.assigned} assigned, {results.failed} failed out of {results.total} total
            </p>
            <Button variant="ghost" size="sm" onClick={() => setResults(null)}>Dismiss</Button>
          </CardContent>
          {results.failed > 0 && (
            <CardContent className="pt-0 pb-3 space-y-1" data-testid="bulk-assign-failure-reasons">
              {(results.results || []).filter((r) => r.status !== 'ASSIGNED').map((r) => (
                <p key={r.shift_id} className="text-xs text-amber-900">
                  <span className="font-mono">{String(r.shift_id).slice(-6)}</span>: {(r.blocks || []).length ? r.blocks.join(' · ') : r.reason}
                </p>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {/* Shifts list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading open shifts...</div>
      ) : openShifts.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No open shifts</p>
          <p className="text-sm mt-1">All shifts are assigned or no shifts match your filters</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {openShifts.map(shift => {
            const isSelected = selectedShifts.has(shift.id);
            const assignedStaff = assignments[shift.id];
            return (
              <Card key={shift.id} className={`transition-all ${isSelected ? 'border-primary ring-1 ring-primary/20' : ''}`} data-testid={`shift-row-${shift.id}`}>
                <CardContent className="py-3 flex items-center gap-4">
                  <input type="checkbox" checked={isSelected} onChange={() => toggleShift(shift.id)}
                    className="rounded h-4 w-4 shrink-0 accent-primary" data-testid={`shift-check-${shift.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium">{shift.date}</span>
                      <span className="text-sm text-muted-foreground">{shift.start_time} - {shift.end_time}</span>
                      <Badge variant="outline" className="text-xs">{shift.shift_type || 'Standard'}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {shift.participant_name || shift.participant_id || 'No participant'} · {shift.branch_id || 'No branch'}
                      {shift.house_name && ` · ${shift.house_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isSelected && (
                      <div className="w-48">
                        <AsyncSearchSelect
                          endpoint="/staff"
                          value={assignedStaff || ''}
                          onChange={v => setStaffForShift(shift.id, v)}
                          getOptionLabel={staffLabel}
                          selectedLabel={labelFor(staff, assignedStaff, staffLabel)}
                          placeholder="Assign staff..."
                          searchPlaceholder={STAFF_SEARCH_HINT}
                          emptyText="No staff match that search"
                          testId={`staff-select-${shift.id}`}
                        />
                      </div>
                    )}
                    {assignedStaff && (
                      <Badge variant="default" className="text-xs"><Users className="w-3 h-3 mr-1" />{getStaffName(assignedStaff)}</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
